import { memo, useMemo } from "react";
import { Chip, Paper, Stack, Typography } from "@mui/material";
import { useAvailableTeleconsultoria } from "../../hooks/useAvailableTeleconsultoria";
import { PRESENCE_CHIP } from "../../presence/presenceChip";
import { useTeleconsultoriaPresence } from "../../presence/useTeleconsultoriaPresence";
import { isSamePractitionerReference } from "../../utils/actors";
import { ActivitySectionFrame } from "./ActivitySectionFrame";

/**
 * Disponibilidade da equipe (SÓ LEITURA): quem está com a disponibilidade
 * ligada e se está online. A clínica não liga/desliga a de outro profissional;
 * cada um controla a sua na visão profissional.
 */
export const TeamAvailabilitySection = memo(function TeamAvailabilitySection({
  professionalReference,
}: {
  professionalReference?: string;
}) {
  const { rows, loading, reload } = useAvailableTeleconsultoria();
  const visibleRows = useMemo(
    () =>
      professionalReference
        ? rows.filter((row) =>
            isSamePractitionerReference(row.guest.practitioner.reference, professionalReference),
          )
        : rows,
    [rows, professionalReference],
  );
  const presence = useTeleconsultoriaPresence([
    ...new Set(visibleRows.map((row) => row.guest.practitioner.reference)),
  ]);

  return (
    <ActivitySectionFrame
      title="Disponibilidade da equipe"
      subtitle="Quem está disponível para teleconsultoria agora."
      isLoading={loading}
      hasError={false}
      errorText=""
      isEmpty={!visibleRows.length}
      emptyText="Nenhum profissional com a disponibilidade ligada."
      onRefresh={reload}
    >
      {visibleRows.map((row) => {
        const chip = PRESENCE_CHIP[presence[row.guest.practitioner.reference] ?? "checking"];
        return (
          <Paper key={row.scheduleId} variant="outlined" role="listitem" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {row.guest.practitioner.display || "Profissional"}
                </Typography>
                {row.guest.healthcareService?.display && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {row.guest.healthcareService.display}
                  </Typography>
                )}
              </Stack>
              <Chip size="small" label={chip.label} color={chip.color} />
            </Stack>
          </Paper>
        );
      })}
    </ActivitySectionFrame>
  );
});
